import { useState } from "react";
import { ReviewStatus, type RowEdit } from "@models/job";
import type { MappingResultRow } from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";

export interface UseResultRowResult {
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
  effectiveStatus: ReviewStatus;
  isFailed: boolean;
  isSaving: boolean;
  actionsDisabled: boolean;
  /** True when the row has candidates to offer. The count is gone: the list
   *  payload says only THAT they exist, and the candidates themselves load on
   *  demand — so a number here would have been a second, staler source. */
  hasAlternative: boolean;
}

/**
 * Shared per-row state and derived values for both result tables: the
 * effective status, the save/disabled flags, and the history drawer's open
 * state.
 */
export const useResultRow = (
  row: MappingResultRow,
  edit: RowEdit | undefined,
  saveState: RowSaveStatus | undefined,
): UseResultRowResult => {
  const [historyOpen, setHistoryOpen] = useState(false);

  const effectiveStatus = edit?.reviewStatus ?? row.reviewStatus;
  const isFailed = row.reviewStatus === ReviewStatus.Failed;
  const isSaving = saveState?.state === "saving";
  const actionsDisabled = isFailed || isSaving;

  return {
    historyOpen,
    setHistoryOpen,
    effectiveStatus,
    isFailed,
    isSaving,
    actionsDisabled,
    hasAlternative: row.hasAlternative,
  };
};
